import { HttpClient, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import {
  Directive,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { combineLatest, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { SummernoteOptions } from './summernote-options';
import { codeBlockButton } from './code-block.button';
import { AlertService } from 'app/services/alert.service';


declare var $;

@Directive({
  // tslint:disable-next-line:directive-selector
  selector: '[ungxSummernote]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UNgxSummernoteDirective),
      multi: true
    }
  ]
})
export class UNgxSummernoteDirective
  implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
  @Input() set ungxSummernote(options: SummernoteOptions) {
    if (options) {
      if (!options.buttons) {
        options.buttons = {};
      }

      options.callbacks = {
        ...options.callbacks,
        onImageUpload: files => this.uploadImage(files),
        onMediaDelete: files =>
          this.mediaDelete.emit({ url: $(files[0]).attr('src') })
      };

      // add custom buttons
      options.buttons.codeBlock = codeBlockButton;

      Object.assign(this._options, options);
    }
  }

  // summernoteModel directive as input: store initial editor content
  @Input() set summernoteModel(content: any) {
    this.updateEditor(content);
  }

  // summernoteModel directive as output: update model if editor contentChanged
  @Output() summernoteModelChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() imageUpload: EventEmitter<any> = new EventEmitter<any>();
  @Output() mediaDelete: EventEmitter<any> = new EventEmitter<any>();

  // // summernoteInit directive as output: send manual editor initialization
  // @Output() summernoteInit: EventEmitter<Object> = new EventEmitter<Object>();

  @Output() blur: EventEmitter<any> = new EventEmitter<any>();

  @Input() ngxSummernoteDisabled: boolean = false;

  private _options: SummernoteOptions = {};

  private SPECIAL_TAGS: string[] = ['img', 'button', 'input', 'a'];
  private INNER_HTML_ATTR = 'innerHTML';
  private _hasSpecialTag: boolean = false;
  private _$element: any; // jquery wrapped element
  private _editor: any; // editor element
  private _model?: string;
  private _oldModel: string|null = null;
  private _editorInitialized: boolean = false;

  private uploadSub?: Subscription;

  constructor(
    private el: ElementRef,
    private zone: NgZone,
    private http: HttpClient,
    private _alertService: AlertService
  ) {
    const element: any = el.nativeElement;

    // check if the element is a special tag
    if (this.SPECIAL_TAGS.indexOf(element.tagName.toLowerCase()) !== -1) {
      this._hasSpecialTag = true;
    }

    // jquery wrap and store element

    // this._$element = <any>$(element);

    this.zone = zone;
  }

  ngOnInit() {
    this.createEditor();
  }

  ngOnChanges(changes) {
    if (this._editorInitialized && changes) {
      if (
        changes.ngxSummernoteDisabled &&
        !changes.ngxSummernoteDisabled.firstChange &&
        changes.ngxSummernoteDisabled.currentValue !==
          changes.ngxSummernoteDisabled.previousValue
      ) {
        if (changes.ngxSummernoteDisabled.currentValue) {
          this._$element.summernote('disable');
        } else {
          this._$element.summernote('enable');
        }
      }
    }
  }

  ngOnDestroy() {
    this.destroyEditor();
    if (this.uploadSub) {
      this.uploadSub.unsubscribe();
    }
  }

  // Begin ControlValueAccesor methods.
  onChange = (_: any) => {};
  onTouched = () => {};

  // Form model content changed.
  writeValue(content: any): void {
    this.updateEditor(content);
  }

  registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Update editor with model contents.
  private updateEditor(content: any) {
    if (JSON.stringify(this._oldModel) === JSON.stringify(content)) {
      return;
    }

    this._oldModel = content;
    // this._$element.html(content);

    // Normalize empty content to prevent <p><br></p> initialization
    const normalizedContent = (!content || content === '<p><br></p>' || content === '<p><br/></p>') ? '' : content;

    if (this._editorInitialized) {
      this._$element.summernote('code', normalizedContent);
    } else {
      this._$element.html(normalizedContent);
    }
  }

  // update model if editor contentChanged
  private updateModel(content?: any) {
    // console.log('update model', content)
    this.zone.run(() => {
      let modelContent: any = null;

      if (this._hasSpecialTag) {
        const attributeNodes = this._$element[0].attributes;
        const attrs = {};

        for (let i = 0; i < attributeNodes.length; i++) {
          const attrName = attributeNodes[i].name;
          if (
            this._options.angularIgnoreAttrs &&
            this._options.angularIgnoreAttrs.indexOf(attrName) !== -1
          ) {
            continue;
          }
          attrs[attrName] = attributeNodes[i].value;
        }

        if (this._$element[0].innerHTML) {
          attrs[this.INNER_HTML_ATTR] = this._$element[0].innerHTML;
        }

        modelContent = attrs;
      } else {
        const returnedHtml: any = content || '';
        if (typeof returnedHtml === 'string') {
          // Normalize empty Summernote content to empty string
          modelContent = (returnedHtml === '<p><br></p>' || returnedHtml === '<p><br/></p>') ? '' : returnedHtml;
        }
      }
      this._oldModel = modelContent;
      // Update summernoteModel
      this.summernoteModelChange.emit(modelContent);
      // Update form model.
      this.onChange(modelContent);
    });
  }

  private initListeners() {
    const self = this;

    if (!this._$element) {
      return;
    }

    this._$element.on('summernote.init', function() {
      setTimeout(function() {
        self.updateModel();
      }, 0);
    });

    this._$element.on('summernote.change', function(
      event,
      contents,
      $editable
    ) {
      setTimeout(function() {
        self.updateModel(contents);
      }, 0);
    });

    this._$element.on('summernote.blur', function() {
      setTimeout(function() {
        self.onTouched();
        self.blur.emit();
      }, 0);
    });

    if (this._options.immediateAngularModelUpdate) {
      this._editor.on('keyup', function() {
        setTimeout(function() {
          self.updateModel();
        }, 0);
      });
    }
  }

  private createEditor() {
    if (this._editorInitialized) {
      return;
    }

    this.setContent(true);

    const wait = 50;
    // this.initListeners(); // issue #31
    try {
      this._$element = <any>$(this.el.nativeElement);
    } catch (error) {
      console.log(`JQuery seems not te loaded yet! Wait ${wait}ms and try again`);
    }

    if (!this._$element) {
      setTimeout(() => {
        this.createEditor();
      }, wait);
    } else {
      // init editor
      this.zone.runOutsideAngular(() => {
        this._editor = this._$element
          .summernote(this._options)
          .data('summernote').$note;

        this.initListeners(); // issue #31

        if (this.ngxSummernoteDisabled) {
          this._$element.summernote('disable');
        }
      });
      this._editorInitialized = true;
    }
  }

  private setHtml() {
    this._$element.summernote('code', this._model || '', true);
  }

  private setContent(firstTime = false) {
    // console.log('set content', firstTime, this._oldModel, this._model)
    const self = this;
    // Set initial content
    if (this._model || this._model === '') {
      this._oldModel = this._model;
      if (this._hasSpecialTag) {
        const tags: Object = this._model;
        // add tags on element
        if (tags) {
          for (const attr in tags) {
            if (tags.hasOwnProperty(attr) && attr !== this.INNER_HTML_ATTR) {
              this._$element.attr(attr, tags[attr]);
            }
          }

          if (tags.hasOwnProperty(this.INNER_HTML_ATTR)) {
            this._$element[0].innerHTML = tags[this.INNER_HTML_ATTR];
          }
        }
      } else {
        self.setHtml();
      }
    }
  }

  private destroyEditor() {
    if (this._editorInitialized) {
      this._editor.off('keyup');
      this._$element.summernote('destroy'); // TODO not sure it works now...
      this._editorInitialized = false;
    }
  }

  // private getEditor() {
  //   if (this._$element) {
  //     return this._$element.summernote.bind(this._$element);
  //   }

  //   return null;
  // }
  finished = 0;
  requests=[] as any[]
  private async uploadImage(files)  {
    if (this._options.uploadImagePath) {
      

      this.requests = [] as any[];
      this.finished = 0
      
      for (const file of files) {
        
        if (!this._options.maximumImageFileSize || file.size <= this._options.maximumImageFileSize) {
            const data = new FormData();
            data.append('image', file);
              const obs = this.http.post(this._options.uploadImagePath, data,{reportProgress:true, observe:'events'}).subscribe({next:async (event:any) => {
                if (event.type === HttpEventType.UploadProgress) {
                  // Calculate and track upload progress here
                  const percentDone = Math.round((100 * event.loaded) / event.total);
                //   console.log(`Upload progress for ${file.name}: ${percentDone}%`);
                } else if (event.type === HttpEventType.Response) {
                  // Handle the response when the upload is complete
                  const response = event.body;
                  this.finished++;
                  if (response && typeof response.path === 'string') {
                    // console.log(`Upload completed for ${file.name}. Path: ${response.path}`);
                    await this._$element.summernote('insertImage', response.path);
                  }
                  if(this.requests.length==this.finished){
                    // console.log("All uploads completed!");
                    this.imageUpload.emit({ uploading: false });
                  }
                }
              }, error:(err:any)=>{
                // Handle upload error
                this.finished++;
                const errorMessage = err?.error?.error?.message || err?.message || 'Erro ao fazer upload da imagem';
                this._alertService.error(`Erro ao fazer upload de ${file.name}: ${errorMessage}`);

                // Check if all uploads are done (including failed ones)
                if(this.requests.length==this.finished){
                  this.imageUpload.emit({ uploading: false });
                }
              }});

            this.requests.push(obs);
        }else{
            // Handle the case where the file exceeds 5 MB (e.g., show an error message)
            this._alertService.error(`O arquivo ${file.name} excede o limite máximo de ${this._options.maximumImageFileSize/(1024*1024)} MB`)
            // this.onChange(this)
        }

        if(this.requests.length>0){
            this.imageUpload.emit({ uploading: true });
        }
        
      }
    } else {
      this.insertFromDataURL(files);
    }
  }

  insertFromDataURL(files) {
    for (const file of files) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        this._$element.summernote('insertImage', reader.result);
        this.imageUpload.emit({ uploading: false, encoding: 'base64' });
      };
      reader.onerror = error => console.error(error);
    }
  }
}